import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockResetRounded,
  PersonAddRounded,
  RefreshRounded,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  type FormEvent,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getTenantUsers,
  createTenantUser,
  updateTenantUser,
  deleteTenantUser,
  resetTenantUserPassword,
  type TenantUserRecord,
} from "../api/tenant-users";
import { getTenants, type TenantRecord } from "../api/tenants";
import { useNotifications } from "../components/NotificationProvider";

type CreateFormState = {
  tenant_id: string;
  email: string;
  password: string;
};

type EditFormState = {
  email: string;
  is_active: boolean;
};

type ResetPasswordFormState = {
  password: string;
  confirm_password: string;
};

const createInitialCreateForm = (): CreateFormState => ({
  tenant_id: "",
  email: "",
  password: "",
});

const createInitialEditForm = (): EditFormState => ({
  email: "",
  is_active: true,
});

const createInitialResetForm = (): ResetPasswordFormState => ({
  password: "",
  confirm_password: "",
});

export function TenantUsersPage({ token }: { token: string }) {
  const [users, setUsers] = useState<TenantUserRecord[]>([]);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [isLoading, setIsLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUserRecord | null>(
    null,
  );
  const [createForm, setCreateForm] = useState<CreateFormState>(
    createInitialCreateForm,
  );
  const [editForm, setEditForm] = useState<EditFormState>(
    createInitialEditForm,
  );
  const [resetForm, setResetForm] = useState<ResetPasswordFormState>(
    createInitialResetForm,
  );
  const { showAlert, showSnackbar } = useNotifications();

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [usersData, tenantsData] = await Promise.all([
        getTenantUsers(token),
        getTenants(),
      ]);
      setUsers(usersData);
      setTenants(tenantsData);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load data",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [token]);

  const tenantIdsWithUsers = useMemo(
    () => new Set(users.map((u) => u.tenant_id)),
    [users],
  );

  const availableTenants = useMemo(
    () => tenants.filter((t) => !tenantIdsWithUsers.has(t.id) && !t.archived),
    [tenants, tenantIdsWithUsers],
  );

  const filteredUsers = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) => {
      const haystack = [
        user.first_name,
        user.last_name,
        user.email,
        user.phone ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [deferredSearchTerm, users]);

  const openCreateModal = () => {
    setCreateForm(createInitialCreateForm());
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!createForm.tenant_id) {
      showAlert({
        title: "Missing tenant",
        message: "Please select a tenant.",
        variant: "error",
      });
      return;
    }
    if (!createForm.email.trim()) {
      showAlert({
        title: "Missing email",
        message: "Email is required.",
        variant: "error",
      });
      return;
    }
    if (!createForm.password || createForm.password.length < 6) {
      showAlert({
        title: "Weak password",
        message: "Password must be at least 6 characters.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      await createTenantUser(token, {
        tenant_id: Number(createForm.tenant_id),
        email: createForm.email.trim(),
        password: createForm.password,
      });
      setCreateModalOpen(false);
      showSnackbar({
        title: "User created",
        message: "Tenant user account has been created.",
        variant: "success",
      });
      await loadData();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to create user";
      showAlert({ title: "Creation failed", message, variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (user: TenantUserRecord) => {
    setSelectedUser(user);
    setEditForm({ email: user.email, is_active: user.is_active });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedUser) return;

    if (!editForm.email.trim()) {
      showAlert({
        title: "Missing email",
        message: "Email is required.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateTenantUser(token, selectedUser.id, {
        email: editForm.email.trim(),
        is_active: editForm.is_active,
      });
      setEditModalOpen(false);
      setSelectedUser(null);
      showSnackbar({
        title: "User updated",
        message: "Tenant user account has been updated.",
        variant: "success",
      });
      await loadData();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to update user";
      showAlert({ title: "Update failed", message, variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const openResetModal = (user: TenantUserRecord) => {
    setSelectedUser(user);
    setResetForm(createInitialResetForm());
    setResetModalOpen(true);
  };

  const handleResetSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedUser) return;

    if (resetForm.password.length < 6) {
      showAlert({
        title: "Weak password",
        message: "Password must be at least 6 characters.",
        variant: "error",
      });
      return;
    }
    if (resetForm.password !== resetForm.confirm_password) {
      showAlert({
        title: "Passwords don't match",
        message: "The passwords entered do not match.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      await resetTenantUserPassword(token, selectedUser.id, resetForm.password);
      setResetModalOpen(false);
      setSelectedUser(null);
      showSnackbar({
        title: "Password reset",
        message: "The password has been successfully reset.",
        variant: "success",
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to reset password";
      showAlert({ title: "Reset failed", message, variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirmation = (user: TenantUserRecord) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;

    setDeleteDialogOpen(false);
    setIsLoading(true);
    try {
      await deleteTenantUser(token, selectedUser.id);
      showSnackbar({
        title: "User deleted",
        message: "The tenant user account has been removed.",
        variant: "info",
      });
      await loadData();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to delete user";
      showAlert({ title: "Delete failed", message, variant: "error" });
    } finally {
      setIsLoading(false);
      setSelectedUser(null);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", position: "relative" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -8,
            left: 0,
            right: 0,
            borderRadius: 6,
          }}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 2, md: 3 },
          bgcolor: "background.paper",
          boxShadow: "0 10px 35px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, email, or phone..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              maxHeight: 40,
              "& .MuiOutlinedInput-root": { borderRadius: 6 },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => void loadData()}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 6,
                }}
              >
                <RefreshRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<PersonAddRounded />}
              onClick={openCreateModal}
              disableElevation
              sx={{ borderRadius: 6, px: 3, whiteSpace: "nowrap", height: 40 }}
            >
              Create user
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 2, md: 2.5 },
          bgcolor: "rgba(33, 82, 184, 0.03)",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Tenant accounts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage login credentials for tenants. Each tenant can have one
              account to access the tenant portal.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Chip
              label={`${users.length} accounts`}
              color="primary"
              variant="filled"
              sx={{ borderRadius: 6, fontWeight: 600 }}
            />
            <Chip
              label={`${users.filter((u) => u.is_active).length} active`}
              color="success"
              variant="outlined"
              sx={{ borderRadius: 6, fontWeight: 600 }}
            />
            <Chip
              label={`${users.filter((u) => !u.is_active).length} disabled`}
              variant="outlined"
              sx={{ borderRadius: 6, fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 6, p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Tenant Users ({filteredUsers.length})
        </Typography>

        <Stack spacing={2}>
          {filteredUsers.map((user) => (
            <Card
              key={user.id}
              variant="outlined"
              sx={{
                borderRadius: 6,
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow: 2,
                  borderColor: "primary.main",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: user.is_active ? "primary.main" : "grey.400",
                        width: 42,
                        height: 42,
                        fontWeight: 700,
                      }}
                    >
                      {user.first_name?.charAt(0) ?? "T"}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {user.first_name} {user.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1.25, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={user.is_active ? "Active" : "Disabled"}
                      size="small"
                      color={user.is_active ? "success" : "default"}
                      variant={user.is_active ? "filled" : "outlined"}
                      sx={{ borderRadius: 6, fontWeight: 600 }}
                    />
                    {user.phone && (
                      <Chip
                        label={user.phone}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 6, fontWeight: 600 }}
                      />
                    )}
                    {user.pending_document_count != null &&
                      user.pending_document_count > 0 && (
                        <Chip
                          label={`${user.pending_document_count} pending docs`}
                          size="small"
                          color="warning"
                          variant="filled"
                          sx={{ borderRadius: 6, fontWeight: 600 }}
                        />
                      )}
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Reset password">
                    <IconButton
                      color="warning"
                      onClick={() => openResetModal(user)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <LockResetRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit user">
                    <IconButton
                      color="primary"
                      onClick={() => openEditModal(user)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteConfirmation(user)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Box sx={{ minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(user.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {filteredUsers.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {users.length === 0
                ? "No tenant user accounts yet. Create one to get started."
                : "No users match your current search."}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Create User Dialog */}
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 520 } }}
      >
        <DialogTitle>Create tenant user</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a login account for a tenant. They will use these credentials
            to access the tenant portal.
          </DialogContentText>
          <Box
            component="form"
            id="create-user-form"
            onSubmit={handleCreateSubmit}
            noValidate
          >
            <Stack spacing={2.25}>
              <TextField
                select
                label="Tenant"
                value={createForm.tenant_id}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    tenant_id: event.target.value,
                  }))
                }
                fullWidth
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              >
                <MenuItem value="">
                  <em>Select a tenant</em>
                </MenuItem>
                {availableTenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.first_name} {tenant.last_name}
                  </MenuItem>
                ))}
                {availableTenants.length === 0 && (
                  <MenuItem value="" disabled>
                    All tenants already have accounts
                  </MenuItem>
                )}
              </TextField>

              <TextField
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                placeholder="tenant@email.com"
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />

              <TextField
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
                placeholder="Minimum 6 characters"
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateModalOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-user-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Create account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 520 } }}
      >
        <DialogTitle>Edit tenant user</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the account details for{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            .
          </DialogContentText>
          <Box
            component="form"
            id="edit-user-form"
            onSubmit={handleEditSubmit}
            noValidate
          >
            <Stack spacing={2.25}>
              <TextField
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.is_active}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                }
                label="Account active"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-user-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 520 } }}
      >
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set a new password for{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            .
          </DialogContentText>
          <Box
            component="form"
            id="reset-password-form"
            onSubmit={handleResetSubmit}
            noValidate
          >
            <Stack spacing={2.25}>
              <TextField
                label="New password"
                type="password"
                value={resetForm.password}
                onChange={(event) =>
                  setResetForm((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
                placeholder="Minimum 6 characters"
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
              <TextField
                label="Confirm password"
                type="password"
                value={resetForm.confirm_password}
                onChange={(event) =>
                  setResetForm((current) => ({
                    ...current,
                    confirm_password: event.target.value,
                  }))
                }
                placeholder="Re-enter the password"
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setResetModalOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="reset-password-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Reset password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 6 } }}
      >
        <DialogTitle>Delete tenant user</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user account for{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            ? The tenant will no longer be able to log in. This does not delete
            the tenant record itself.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
